import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { arrayMove } from '@dnd-kit/sortable';

import guid from './shared/guid';
import {
  SortableList, SortableListRow, keyOf, reorderById, reorderInState, withDndSensors,
} from './shared/dndSortable';
import OptionValue from './fields/OptionValue';

export { reorderById };

class OptionValuesFields extends React.Component {
  static propTypes = {
    initialOptionValues: PropTypes.array.isRequired,
    initialDefaultOptionValueId: PropTypes.number,
    isParent: PropTypes.bool,
  };

  static defaultProps = {
    initialDefaultOptionValueId: null,
    isParent: false,
  };

  constructor(props) {
    super(props);
    const { initialOptionValues, initialDefaultOptionValueId } = props;
    let defaultOptionKey = null;
    if (initialDefaultOptionValueId) {
      const matched = initialOptionValues.find((ov) => ov.id === initialDefaultOptionValueId);
      defaultOptionKey = matched ? keyOf(matched) : null;
    }
    this.state = {
      optionValues: initialOptionValues,
      bulkUpdateMode: false,
      defaultOptionKey,
    };
  }

  // Required for OptionValue tooltips
  componentDidMount() {
    $('.tool-tip').tooltip({ container: 'body' });
  }

  onDragEnd = (event) => reorderInState(event, this, 'optionValues', this.recalculatePositions);

  // All non-DnD handlers remain identical to the prior implementation:
  handleRemoveOptionValue = (index) => {
    const removedOptionValue = this.state.optionValues[index];
    const newOptionValues = update(this.state.optionValues, {
      [index]: { markedForDistruction: { $set: true } },
    });
    const removedKey = keyOf(removedOptionValue);
    const newDefaultOptionKey =
      removedKey === this.state.defaultOptionKey ? null : this.state.defaultOptionKey;
    this.setState(
      { optionValues: newOptionValues, defaultOptionKey: newDefaultOptionKey },
      this.recalculatePositions
    );
  };

  handleDefaultChange = (index) => {
    const optionValue = this.state.optionValues[index];
    this.setState({ defaultOptionKey: keyOf(optionValue) });
  };

  handleDefaultClick = (index) => {
    const optionValue = this.state.optionValues[index];
    const clickedKey = keyOf(optionValue);
    if (clickedKey === this.state.defaultOptionKey) {
      this.setState({ defaultOptionKey: null });
    } else {
      this.setState({ defaultOptionKey: clickedKey });
    }
  };

  handleOptionValueNameChange = (index, value) => {
    const newOptionValues = update(this.state.optionValues, {
      [index]: { name: { $set: value } },
    });
    this.setState({ optionValues: newOptionValues });
  };

  handleOptionValueDescriptionChange = (index, value) => {
    const newOptionValues = update(this.state.optionValues, {
      [index]: { description: { $set: value } },
    });
    this.setState({ optionValues: newOptionValues });
  };

  handleAddOptionValue = () => {
    const newOptionValues = update(this.state.optionValues, {
      $push: [{ key: guid(), name: '', description: '', markedForDistruction: false }],
    });
    this.setState({ optionValues: newOptionValues }, this.recalculatePositions);
  };

  handleSortOptionValue = () => {
    const newOptionValues = update(this.state.optionValues, {});
    newOptionValues.sort((a, b) => a.name.localeCompare(b.name));
    this.setState({ optionValues: newOptionValues }, this.recalculatePositions);
  };

  recalculatePositions = () => {
    let positionCounter = 0;
    const newOptionValues = this.state.optionValues.map((optionValue) => {
      if (!optionValue.markedForDistruction) positionCounter += 1;
      optionValue.position = positionCounter;
      return optionValue;
    });
    this.setState({ optionValues: newOptionValues });
  };

  handleBulkUpdateMode = () => {
    this.setState({
      bulkUpdateMode: true,
      bulkUpdateValues: this.initialBulkUpdateValues(),
    });
  };

  initialBulkUpdateValues = () => {
    let initialValue = '';
    for (const optionValue of this.visibleOptionValues()) {
      initialValue += `${optionValue.name},${optionValue.description}\n`;
    }
    return initialValue;
  };

  handleBulkUpdateCancel = () => {
    this.setState({ bulkUpdateMode: false });
  };

  handleBulkUpdateValues = () => {
    let newOptionValues = this.markAllOptionValuesForDestruction();
    const parsedOptionValues = this.parsedBulkUpdateOptionValues();

    for (const [index, parsedOptionValue] of parsedOptionValues.entries()) {
      const existingValueIndex = this.findOptionValueByName(newOptionValues, parsedOptionValue.name);
      const existingOptionValue = newOptionValues[existingValueIndex];

      if (typeof existingValueIndex === 'number' && existingOptionValue.markedForDistruction) {
        newOptionValues = update(newOptionValues, {
          [existingValueIndex]: {
            markedForDistruction: { $set: false },
            description: { $set: parsedOptionValue.description },
          },
        });
        newOptionValues = arrayMove(newOptionValues, existingValueIndex, index);
      } else if (parsedOptionValue.name.length > 0) {
        const newOptionValue = {
          key: guid(),
          name: parsedOptionValue.name,
          description: parsedOptionValue.description,
          markedForDistruction: false,
        };
        newOptionValues = update(newOptionValues, { $splice: [[index, 0, newOptionValue]] });
      }
    }

    this.setState(
      { optionValues: newOptionValues, bulkUpdateMode: false },
      this.recalculatePositions
    );
  };

  markAllOptionValuesForDestruction = () => {
    let newOptionValues = update(this.state.optionValues, {});
    for (let index = 0; index < this.state.optionValues.length; index += 1) {
      newOptionValues = update(newOptionValues, {
        [index]: { markedForDistruction: { $set: true } },
      });
    }
    return newOptionValues;
  };

  findOptionValueByName = (optionValues, name) => {
    for (const [index, optionValue] of optionValues.entries()) {
      if (optionValue.name === name) return index;
    }
    return undefined;
  };

  parsedBulkUpdateOptionValues = () => {
    const rows = this.state.bulkUpdateValues.split(/\n/);
    const parsedOptionValues = [];
    for (const row of rows) {
      const attributes = row.split(',');
      const name = (attributes[0] || '').trim();
      const description = (attributes[1] || '').trim();
      if (name.length > 0) parsedOptionValues.push({ name, description });
    }
    return parsedOptionValues;
  };

  handleBulkUpdateValuesChange = (event) => {
    this.setState({ bulkUpdateValues: event.target.value });
  };

  hasDuplicateOptionValues = () => {
    const self = this;
    const names = this.visibleOptionValues().map((item) => item.name);
    const duplicateValues = names.filter((item, index) => {
      const duplicate = names.indexOf(item) !== index;
      self.visibleOptionValues()[index].duplicate = duplicate;
      return duplicate;
    });
    if (duplicateValues.length > 0) {
      document.getElementById('save-field').disabled = true;
    } else {
      document.getElementById('save-field').disabled = false;
    }
    return duplicateValues.length > 0;
  };

  hasChildFields = () =>
    this.visibleOptionValues().some(
      (optionValue) => optionValue.childFields && optionValue.childFields.length > 0
    );

  visibleOptionValues = () =>
    this.state.optionValues.filter((item) => item && !item.markedForDistruction);

  render() {
    const itemIds = this.state.optionValues.map(keyOf);
    const dragHandleWidthNode = <div className="col-xs-1" style={{ width: '30px' }}></div>;

    let errorNode;
    if (this.hasDuplicateOptionValues()) {
      errorNode = (
        <div className="row mb-10">
          {dragHandleWidthNode}
          <div className="col-xs-11">
            <span className="text-danger">
              Duplicate values found. Please make sure all values are unique
            </span>
          </div>
        </div>
      );
    }

    const normalModeNode = (
      <div>
        <div className="row">
          {dragHandleWidthNode}
          <div className="col-xs-1" style={{ width: '30px' }}></div>
          <div className="col-xs-10">
            <ul className="list-inline">
              <li>
                <a onClick={this.handleSortOptionValue}>
                  <i className="icon-sort-alpha-asc position-left"></i>
                  Sort
                </a>
              </li>
              <li>
                <a onClick={this.handleBulkUpdateMode}>
                  <i className="icon-pencil3 position-left"></i>
                  Bulk update
                </a>
              </li>
            </ul>
          </div>
        </div>

        <SortableList sensors={this.props.__sensors} items={itemIds} onDragEnd={this.onDragEnd}>
          {this.state.optionValues.map((optionValue, index) => {
            const isDefault = keyOf(optionValue) === this.state.defaultOptionKey;
            return (
              <SortableListRow
                key={keyOf(optionValue)}
                id={keyOf(optionValue)}
                className="option-value row mb-10"
                style={{ display: optionValue.markedForDistruction ? 'none' : 'block' }}
                handleClassName="col-xs-1"
                handleStyle={{ width: '30px' }}
              >
                <OptionValue
                  index={index}
                  optionValue={optionValue}
                  onRemove={this.handleRemoveOptionValue}
                  onNameChange={this.handleOptionValueNameChange}
                  onDescriptionChange={this.handleOptionValueDescriptionChange}
                  isDefault={isDefault}
                  onDefaultChange={this.handleDefaultChange}
                  onDefaultClick={this.handleDefaultClick}
                />
              </SortableListRow>
            );
          })}
        </SortableList>

        {errorNode}

        <div className="row">
          {dragHandleWidthNode}
          <div className="col-xs-11">
            <ul className="list-inline">
              <li>
                <a onClick={this.handleAddOptionValue}>
                  <i className="icon-plus22 position-left"></i>
                  Add value
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );

    let bulkUpdateWarning;
    if (this.hasChildFields()) {
      bulkUpdateWarning = (
        <div className="row mt-10">
          <div className="col-xs-12">
            <span className="text-warning">
              <span className="text-bold">BE CAREFUL : </span>
              One or more option values have dependent fields. Removing or changing them can break this dependency.
            </span>
          </div>
        </div>
      );
    }

    const bulkUpdateNode = (
      <div>
        <div className="row">
          <div className="col-xs-12">
            <textarea
              id="options-bulk-update-textbox"
              className="form-control"
              rows="15"
              value={this.state.bulkUpdateValues}
              onChange={this.handleBulkUpdateValuesChange}
            />
          </div>
        </div>

        {bulkUpdateWarning}

        <div className="row mt-10">
          <div className="col-xs-12">
            <ul className="list-inline">
              <li>
                <a onClick={this.handleBulkUpdateValues}>
                  <i className="icon-pencil3 position-left"></i>
                  Update values
                </a>
              </li>
              <li>
                <a onClick={this.handleBulkUpdateCancel}>
                  <i className="icon-cross3 position-left"></i>
                  Cancel
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );

    const optionsListNode = this.state.bulkUpdateMode ? bulkUpdateNode : normalModeNode;

    let parentDefaultWarningNode;
    if (this.props.isParent && this.state.defaultOptionKey != null) {
      parentDefaultWarningNode = (
        <div className="row mt-5 mb-10">
          <div className="col-xs-12">
            <span className="text-warning">
              <span className="text-bold">Note: </span>
              This field has dependent child fields, so the default value will not be applied when the form is filled out.
            </span>
          </div>
        </div>
      );
    }

    return (
      <div>
        {parentDefaultWarningNode}
        {optionsListNode}
      </div>
    );
  }
}

export default withDndSensors(OptionValuesFields);
